import { Model } from '../../_kernel/architecture'
import { z } from 'zod'
import { CancellationPolicy } from './CancellationPolicy'
import type { PaymentPolicies } from './PaymentPolicy'
import { PaymentPolicy } from './PaymentPolicy'
import type { Currencies } from '../../_kernel/Currency'
import type { Market } from '../../shared/domain/Market'
import { ListOfAvailabilityReduxLegacyType } from './ListOfAvailabilityReduxLegacySchema'

const ALLOWED_RATE_TYPES = ['RECHECK', 'AT_CONFIRMATION'] as const
export type RateTypes = (typeof ALLOWED_RATE_TYPES)[number]

export const RateValidation = z.object({
  paymentPolicy: z.instanceof(PaymentPolicy, {
    message: '[Rate] paymentPolicy is required',
  }),
  cancellationPolicy: z.instanceof(CancellationPolicy).optional(),
  rateType: z.enum(ALLOWED_RATE_TYPES).optional(),
})

export class Rate extends Model {
  static create({ paymentPolicy, cancellationPolicy, rateType }: z.infer<typeof RateValidation>) {
    RateValidation.parse({ paymentPolicy, cancellationPolicy, rateType })
    return new Rate(paymentPolicy, cancellationPolicy, rateType)
  }

  static fromJSON(json: ReturnType<Rate['toJSON']>) {
    let cancellationPolicy: CancellationPolicy | undefined

    if (json.cancellationPolicy) {
      cancellationPolicy = CancellationPolicy.fromJSON(
        json.cancellationPolicy as ReturnType<CancellationPolicy['toJSON']>
      )
    }

    return new Rate(PaymentPolicy.fromJSON(json.paymentPolicy), cancellationPolicy, json.rateType)
  }

  static fromLiterals({
    paymentPolicy,
    cancellationPolicyFromDate,
    cancellationPolicyAmount,
    currency,
    rateType,
  }: {
    paymentPolicy: PaymentPolicies
    cancellationPolicyFromDate?: string
    cancellationPolicyAmount?: number
    currency?: Currencies
    rateType?: RateTypes
  }) {
    const hasCancellation = cancellationPolicyFromDate && currency

    return Rate.create({
      paymentPolicy: PaymentPolicy.fromLiterals({ value: paymentPolicy }),
      cancellationPolicy: hasCancellation
        ? CancellationPolicy.fromLiterals({
            fromDate: cancellationPolicyFromDate,
            amount: cancellationPolicyAmount ?? 0,
            currency,
          })
        : undefined,
      rateType,
    })
  }

  static fromAvailabilityBoardRedux({ board }: { board: ListOfAvailabilityReduxLegacyType }) {
    return Rate.create({
      paymentPolicy: PaymentPolicy.fromLiterals({ value: board.paymentPolicy as PaymentPolicies }),
      cancellationPolicy: CancellationPolicy.fromReduxBoardsOrNull({ board }) ?? undefined,
      rateType: board.isRecheck ? 'RECHECK' : undefined,
    })
  }

  constructor(
    private readonly _paymentPolicy: PaymentPolicy,
    private readonly _cancellationPolicy?: CancellationPolicy,
    private readonly _rateType?: RateTypes
  ) {
    super()
  }

  paymentPolicy() {
    return this._paymentPolicy.value
  }

  cancellationPolicyFromDate() {
    return this._cancellationPolicy?.fromDate ?? ''
  }

  cancellationDateFormatted({ locale }: { locale: string }) {
    return this._cancellationPolicy?.getFromDateFormatted(locale)
  }

  cancellationPolicyAmount() {
    return this._cancellationPolicy?.amount()
  }

  cancellationPriceFormatted({ locale, market }: { locale: string; market: Market }) {
    return this._cancellationPolicy?.getPriceFormatted(locale, market)
  }

  hasCancellationPolicy() {
    return this._cancellationPolicy !== undefined
  }

  currency() {
    return this._cancellationPolicy?.currency().value
  }

  isRecheck() {
    return this._rateType === 'RECHECK'
  }

  isNOR() {
    return this._paymentPolicy.value === 'NOR'
  }

  isSFL() {
    return this._paymentPolicy.value === 'SFL'
  }

  isNRF() {
    return this._paymentPolicy.value === 'NRF'
  }

  isNORE() {
    return this._paymentPolicy.value === 'NORE'
  }

  toJSON() {
    return {
      paymentPolicy: this._paymentPolicy.toJSON(),
      cancellationPolicy: this._cancellationPolicy ? this._cancellationPolicy.toJSON() : null,
      ...(this._rateType && { rateType: this._rateType }),
    }
  }
}
